<script setup>
import { computed, onMounted, ref } from 'vue';
import TeamCard from '@/components/teams/TeamCard.vue';
import MatchSummary from '@/components/scoring/MatchSummary.vue';
import { useScoringApi } from '@/composables/useScoringApi';
import { useTimelineModels } from '@/composables/useTimelineModels';

// Post-match detail on the fixture page. Preferred source: the fixture's FINALISED live
// scoring session — its frozen schema snapshot + final state reproduce the full live
// summary (game-by-game progression, breaks/fouls, highlight totals) regardless of later
// changes to the scoring system. Fallback: the saved FixtureEvent rows (self-describing;
// labels baked into metadata at finalisation) for manually tagged events.
const props = defineProps({
  fixture: { type: Object, required: true },
});

const { teamName } = useTimelineModels();
const homeName = computed(() => teamName(props.fixture?.homeTeam));
const awayName = computed(() => teamName(props.fixture?.awayTeam));

const sessionState = ref(null);
const sessionSchema = ref(null);

const { items: events, getItems } = useCrudApi('api/v2/fixtureevents');
onMounted(async () => {
  try {
    const session = await useScoringApi().getFinalSessionForFixture(props.fixture.id);
    if (session?.state && session?.schemaSnapshot) {
      sessionState.value = JSON.parse(session.state);
      sessionSchema.value = JSON.parse(session.schemaSnapshot);
    }
  } catch {
    // unauthenticated or transient — the FixtureEvent fallback still renders
  }
  if (!sessionState.value) await getItems(1, 500, { fixtureIds: [props.fixture.id] });
});

const teamFor = (teamId) =>
    teamId === props.fixture.homeTeam?.id ? props.fixture.homeTeam
        : teamId === props.fixture.awayTeam?.id ? props.fixture.awayTeam : null;

const metadataOf = (event) => {
  try {
    return event.metadataJson ? JSON.parse(event.metadataJson) : {};
  } catch {
    return {};
  }
};

// "frame:2" -> "Frame 2" — the unit name was frozen with the row
const unitRefLabel = (unitRef) => {
  const last = unitRef?.split('/').pop();
  if (!last) return null;
  const [name, index] = last.split(':');
  const label = name.charAt(0).toUpperCase() + name.slice(1);
  return index ? `${label} ${index}` : label;
};

// "highCheckout" -> "High checkout" (fallback for rows saved without a label)
const humanize = (key) => {
  const spaced = key.replace(/([A-Z])/g, ' $1').toLowerCase().trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

// break/foul rows are the accumulator play-by-play; everything else totals per team
const playByPlay = computed(() =>
    (events.value ?? [])
        .filter((e) => e.eventKey === 'break' || e.eventKey === 'foul')
        .sort((a, b) => a.sequence - b.sequence)
        .map((e) => ({
          key: e.id,
          team: teamFor(e.teamId),
          text: e.eventKey === 'break' ? `Break ${e.value ?? 0}` : `Foul +${e.value ?? 0}`,
          isFoul: e.eventKey === 'foul',
          unitLabel: unitRefLabel(e.unitRef),
        })));

// consecutive rows from the same frame sit under one "Frame 1" card
const playByPlayGroups = computed(() => {
  const groups = [];
  for (const row of playByPlay.value) {
    const label = row.unitLabel ?? '';
    const last = groups[groups.length - 1];
    if (!last || last.label !== label) groups.push({ label, rows: [row] });
    else last.rows.push(row);
  }
  return groups;
});

const highlightTotals = computed(() => {
  const byKey = new Map();
  for (const e of events.value ?? []) {
    if (e.eventKey === 'break' || e.eventKey === 'foul') continue;
    const side = e.teamId === props.fixture.homeTeam?.id ? 'home'
        : e.teamId === props.fixture.awayTeam?.id ? 'away' : null;
    if (!side) continue;
    let entry = byKey.get(e.eventKey);
    if (!entry) {
      entry = {
        key: e.eventKey,
        label: metadataOf(e).label ?? humanize(e.eventKey),
        home: [],
        away: [],
      };
      byKey.set(e.eventKey, entry);
    }
    entry[side].push(e.value != null ? Number(e.value) : null);
  }
  const cell = (values) => {
    if (values.length === 0) return '0';
    const best = Math.max(...values.filter((v) => v != null));
    return Number.isFinite(best) && best > 1 && best !== values.length
        ? `${values.length} · best ${best}`
        : String(values.length);
  };
  return [...byKey.values()].map((entry) => ({
    key: entry.key,
    label: entry.label,
    home: cell(entry.home),
    away: cell(entry.away),
  }));
});

// Goal scorers: the fixture's denormalised per-side lines ("Smith 12', 55' ·
// Jones 30' (og)") — same strings the timeline cards show, kept fresh by the
// API on every event write.
const hasScorers = computed(() =>
    !!props.fixture?.homeScorersSummary || !!props.fixture?.awayScorersSummary);

// One line per scorer, split from the API's " · "-joined summary — the flat
// run-on line was unreadable the moment a 5-a-side match had a few goals.
const scorerLines = (summary) =>
    (summary ?? '').split(' · ').map((line) => line.trim()).filter(Boolean);
const homeScorerLines = computed(() => scorerLines(props.fixture?.homeScorersSummary));
const awayScorerLines = computed(() => scorerLines(props.fixture?.awayScorersSummary));

const hasContent = computed(() =>
    !!sessionState.value || playByPlay.value.length > 0 || highlightTotals.value.length > 0
    || hasScorers.value);
</script>

<template>
  <template v-if="hasContent">
    <div class="tl-section-head"><h2>Match detail</h2></div>

    <!-- goal scorers, per side — football's headline detail. Broadcast style:
         a column per team, one scorer per line. -->
    <div v-if="hasScorers" class="pms__card pms__goals">
      <div class="pms__goals-grid">
        <div v-for="side in ['home', 'away']" :key="side" class="pms__goals-col">
          <div class="pms__goals-team">
            <team-card v-if="side === 'home' ? fixture.homeTeam : fixture.awayTeam"
                       :team="side === 'home' ? fixture.homeTeam : fixture.awayTeam" size="small"
                       :expandable="false" :show-squash-level="false" :max-lines="1" />
          </div>
          <div v-for="(line, i) in (side === 'home' ? homeScorerLines : awayScorerLines)"
               :key="side + i" class="pms__goals-line">
            <v-icon size="13" icon="mdi-soccer" class="pms__goals-ball"/>
            <span>{{ line }}</span>
          </div>
          <div v-if="(side === 'home' ? homeScorerLines : awayScorerLines).length === 0"
               class="pms__goals-none">No goals</div>
        </div>
      </div>
    </div>

    <!-- live-scored fixture: the full summary, from the session's frozen state -->
    <MatchSummary
      v-if="sessionState"
      :state="sessionState"
      :schema="sessionSchema"
      :home-name="homeName"
      :away-name="awayName"
      :home-team="fixture.homeTeam"
      :away-team="fixture.awayTeam"
    />

    <template v-else>
    <!-- highlight totals per team (aces, 180s, checkouts...) -->
    <div v-if="highlightTotals.length" class="pms__card">
      <table class="pms__table">
        <thead>
          <tr>
            <th />
            <th class="pms__th-team">
              <div class="d-flex justify-center">
                <team-card v-if="fixture.homeTeam" :team="fixture.homeTeam" size="small"
                           :minimized="true" :expandable="false" :show-squash-level="false" />
              </div>
            </th>
            <th class="pms__th-team">
              <div class="d-flex justify-center">
                <team-card v-if="fixture.awayTeam" :team="fixture.awayTeam" size="small"
                           :minimized="true" :expandable="false" :show-squash-level="false" />
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in highlightTotals" :key="row.key">
            <td class="pms__cell-label">{{ row.label }}</td>
            <td class="pms__cell-score">{{ row.home }}</td>
            <td class="pms__cell-score">{{ row.away }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- breaks & fouls play-by-play, one card per frame -->
    <div v-for="group in playByPlayGroups" :key="group.label || 'all'" class="pms__card">
      <div v-if="group.label" class="pms__eyebrow">{{ group.label }}</div>
      <table class="pms__table">
        <tbody>
          <tr v-for="row in group.rows" :key="row.key">
            <td class="pms__cell-team">
              <team-card v-if="row.team" :team="row.team" size="small"
                         :expandable="false" :show-squash-level="false" :max-lines="1" />
            </td>
            <td class="pms__cell-event" :class="{'pms__cell-event--foul': row.isFoul}">
              {{ row.text }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    </template>
  </template>
</template>

<style scoped>
/* same card/table language as the live MatchSummary */
.pms__card {
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 14px;
  padding: 8px 0;
  background: rgb(var(--v-theme-surface));
  margin-top: 12px;
  overflow: hidden;
}

/* The goals grid needs real inset (the base card is horizontally flush for its
   full-width rows) and a gap below before the play-by-play cards. */
.pms__goals {
  padding: 12px 14px;
  margin-bottom: 12px;
}

.pms__eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(var(--v-theme-on-surface), 0.55);
  margin: 4px 16px 6px;
}

.pms__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.pms__table td {
  padding: 9px 8px;
  border-top: 1px solid rgb(var(--v-theme-borderLight));
}
.pms__table td:first-child {
  padding-left: 16px;
}
.pms__table td:last-child {
  padding-right: 16px;
}
.pms__table tr:first-child td {
  border-top: none;
}

.pms__th-team {
  width: 64px;
  padding: 4px 8px 6px;
}
.pms__th-team:last-child {
  padding-right: 16px;
}

.pms__cell-label {
  color: rgba(var(--v-theme-on-surface), 0.7);
}
.pms__cell-score {
  text-align: center;
  width: 64px;
  font-variant-numeric: tabular-nums;
}
.pms__cell-team {
  max-width: 0;
  width: 60%;
}
.pms__cell-event {
  text-align: right;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.pms__cell-event--foul {
  color: rgb(var(--v-theme-error));
}

.pms__goals-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 16px;
}

.pms__goals-col:first-child {
  border-right: 1px solid rgb(var(--v-theme-borderLight));
  padding-right: 16px;
}

.pms__goals-team {
  margin-bottom: 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid rgb(var(--v-theme-borderLight));
}

.pms__goals-line {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  padding: 2px 0;
}

.pms__goals-ball {
  color: rgb(var(--v-theme-textSubtle));
}

.pms__goals-none {
  font-size: 12px;
  color: rgb(var(--v-theme-textSubtle));
}
</style>
